import { html, TemplateResult } from "lit-html";
import i18next from "i18next";
import { unsafeHTML } from "lit-html/directives/unsafe-html";

export type PaxType = "adults" | "teens" | "children" | "infants";

export type WarningKey =
	| "groupBooking"
	| "teenInfantRemove"
	| "minorOrInfantAge"
	| "childrenAgeWarningColombia"
	| "teenLegalGuardian";

export interface GetWarningsForCategoryParams {
	type: PaxType;
	passengers: { adults: number; teens: number; children: number; infants: number };
	isWithGroupBookingWarning: boolean;
	isWithTeenInfantRemoveWarning: boolean;
	isColombianDomesticFlight: boolean;
	culture: string;
}

export const passengersUtils = () => {
	const getGroupBookingWarningTemplate = (culture: string) => {
		const [language, country] = culture.toLowerCase().split("-");
		const groupUrl = `/${country}/${language}/minisitios/grupos`;

		return html`
			<div>
				${i18next.t("search-box-group-warning")}<a
					href="${groupUrl}"
					class="ml-1 text-n-cyan underline md:hover:no-underline"
					>${i18next.t("search-box-group-warning-2")}</a
				>
			</div>
		`;
	};

	const getTeenInfantRemoveWarningTemplate = (isChileanSearch: boolean) =>
		html`${unsafeHTML(
			i18next.t(
				"Se ha eliminado un infante de tu búsqueda, ya que los pasajeros entre {{-interval}} años no están habilitados a volar con más de un infante/niño a cargo",
				{
					interval: isChileanSearch ? "14-17" : "13-17",
				}
			)
		)}`;

	const getTeenLegalGuardianWarningTemplate = (isChileanSearch: boolean) =>
		html`${unsafeHTML(
			i18next.t(
				"Recuerda que si viaja un niño y un adolescente ({{-interval}} años), este último debe ser su padre/madre o tutor legal",
				{
					interval: isChileanSearch ? "14-17" : "13-17",
				}
			)
		)}`;

	const getMinorOrInfantAgeWarningTemplate = () =>
		html`${i18next.t("Recuerda que la edad del niño o infante se considera al momento de terminar el viaje")}`;

	const getChildrenAgeWarningColombiaTemplate = () =>
		html`${i18next.t(
			"En base a la ley colombiana, el descuento en la tarifa sólo aplica para los menores de 12 años"
		)}`;

	const createWarningsMap = (culture: string, isChileanSearch: boolean): Map<WarningKey, TemplateResult> =>
		new Map<WarningKey, TemplateResult>([
			["groupBooking", getGroupBookingWarningTemplate(culture)],
			["teenInfantRemove", getTeenInfantRemoveWarningTemplate(isChileanSearch)],
			["teenLegalGuardian", getTeenLegalGuardianWarningTemplate(isChileanSearch)],
			["minorOrInfantAge", getMinorOrInfantAgeWarningTemplate()],
			["childrenAgeWarningColombia", getChildrenAgeWarningColombiaTemplate()],
		]);

	const getWarningsForCategory = (params: GetWarningsForCategoryParams): WarningKey[] => {
		const { type, passengers, isWithGroupBookingWarning, isWithTeenInfantRemoveWarning, isColombianDomesticFlight, culture } = params;
		const warnings: WarningKey[] = [];

		switch (type) {
			case "adults":
				if (isWithGroupBookingWarning) {
					warnings.push("groupBooking");
				}
				break;

			case "teens":
				if (passengers.teens > 0) {
					warnings.push("teenLegalGuardian");
				}
				break;

			case "children":
				if (passengers.children > 0) {
					if (isColombianDomesticFlight && culture.toLowerCase() === "es-co") {
						warnings.push("childrenAgeWarningColombia");
					}
				}
                // DEVNOTE: minorOrInfantAge is not shown here
                // because of rules described in JET-12207 comment (2025.11.24)
				break;

			case "infants":
				if (isWithTeenInfantRemoveWarning) {
					warnings.push("teenInfantRemove");
				}
				if (passengers.infants > 0 || passengers.children > 0) {
					warnings.push("minorOrInfantAge");
				}
				break;
		}

		return warnings;
	};

	return {
		getGroupBookingWarningTemplate,
		getTeenInfantRemoveWarningTemplate,
		getTeenLegalGuardianWarningTemplate,
		getMinorOrInfantAgeWarningTemplate,
		getChildrenAgeWarningColombiaTemplate,
		createWarningsMap,
		getWarningsForCategory,
	};
};
